import { useEffect } from "react";

/**
 * WebMCP (https://webmachinelearning.github.io/webmcp/) — exposes PieFolio's
 * key actions as in-page tools so browser-based AI agents can use the site
 * without scraping the UI. No-ops in browsers without navigator.modelContext.
 */

type ToolDescriptor = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<{
    content: Array<{ type: "text"; text: string }>;
  }>;
};

type ModelContext = {
  registerTool: (tool: ToolDescriptor, options?: { signal?: AbortSignal }) => void;
  provideContext?: (ctx: unknown) => void;
};

const text = (value: unknown) => ({
  content: [{ type: "text" as const, text: typeof value === "string" ? value : JSON.stringify(value) }],
});

const num = (v: unknown, fallback: number) => {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? (n as number) : fallback;
};

export function useWebMcp() {
  useEffect(() => {
    const mc = (navigator as Navigator & { modelContext?: ModelContext }).modelContext;
    if (!mc?.registerTool) return;

    const controller = new AbortController();
    const opts = { signal: controller.signal };

    try {
      mc.registerTool(
        {
          name: "piefolio_list_calculators",
          description:
            "List PieFolio AI's free financial calculators and guides available on this site, with their URLs.",
          inputSchema: { type: "object", properties: {} },
          execute: async () =>
            text({
              calculators: [
                { name: "Retirement calculator", url: "/tools/retirement-calculator" },
                { name: "Dividend calculator", url: "/tools/dividend-calculator" },
                { name: "Coast FIRE calculator", url: "/tools/coast-fire-calculator" },
                { name: "Rent vs buy calculator", url: "/tools/rent-vs-buy-calculator" },
              ],
              guides: [
                { name: "What is Coast FIRE?", url: "/blog/what-is-coast-fire" },
                { name: "How much to retire at 67", url: "/blog/how-much-to-retire-at-67" },
              ],
              apps: {
                ios: "https://apps.apple.com/de/app/piefolio-ai-wealth-tracker/id6759684076",
                android: "https://play.google.com/store/apps/details?id=ai.piefolio.portfolio",
              },
            }),
        },
        opts,
      );

      mc.registerTool(
        {
          name: "piefolio_coast_fire_number",
          description:
            "Calculate a Coast FIRE number: the amount that must already be invested today so the portfolio reaches the FIRE target by retirement age with no further contributions. Educational estimate, not financial advice.",
          inputSchema: {
            type: "object",
            properties: {
              currentAge: { type: "number" },
              retirementAge: { type: "number" },
              annualExpenses: { type: "number" },
              currentInvested: { type: "number" },
              realReturnPct: { type: "number" },
              withdrawalRatePct: { type: "number" },
            },
            required: ["currentAge", "retirementAge", "annualExpenses"],
          },
          execute: async (args) => {
            const years = Math.max(0, num(args.retirementAge, 65) - num(args.currentAge, 26));
            const r = num(args.realReturnPct, 6) / 100;
            const fireNumber = num(args.annualExpenses, 40000) / (num(args.withdrawalRatePct, 4) / 100);
            const coast = fireNumber / Math.pow(1 + r, years);
            const invested = num(args.currentInvested, 0);
            return text({
              yearsToRetirement: years,
              fireNumber: Math.round(fireNumber),
              coastFireNumber: Math.round(coast),
              shortfallToCoast: Math.max(0, Math.round(coast - invested)),
              hasReachedCoastFire: invested >= coast,
              interactive: "/tools/coast-fire-calculator",
              disclaimer: "Educational estimate, not financial advice.",
            });
          },
        },
        opts,
      );

      mc.registerTool(
        {
          name: "piefolio_open_calculator",
          description:
            "Navigate this browser tab to one of PieFolio's calculators so the user can adjust inputs interactively.",
          inputSchema: {
            type: "object",
            properties: {
              tool: {
                type: "string",
                enum: ["retirement", "dividend", "coast-fire", "rent-vs-buy"],
              },
            },
            required: ["tool"],
          },
          execute: async (args) => {
            const map: Record<string, string> = {
              retirement: "/tools/retirement-calculator",
              dividend: "/tools/dividend-calculator",
              "coast-fire": "/tools/coast-fire-calculator",
              "rent-vs-buy": "/tools/rent-vs-buy-calculator",
            };
            const path = map[String(args.tool)] ?? map.retirement;
            window.location.assign(path);
            return text({ navigatedTo: path });
          },
        },
        opts,
      );
    } catch {
      // WebMCP is experimental; failures must never break the page.
    }

    return () => controller.abort();
  }, []);
}

export default useWebMcp;
